import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Clock, Loader2 } from "lucide-react";
import { DIAS, CATEGORIAS, TIPOS_MODALIDAD, VEHICULOS, REGLAS_INFANTE, IDIOMAS } from "@/lib/constants";

const PICKUP_DEFAULT = "Esquina 7th Ave & W 51st St, New York, NY 10019, EE. UU.";

const emptyTour = () => ({
  nombre: "",
  categoria: "dentro_ciudad",
  idiomas: ["es"],
  punto_recogida: PICKUP_DEFAULT,
  estacionalidad: { todo_el_anio: true, desde: "", hasta: "" },
  activo: true,
  politica_cancelacion_horas: "",
  regla_infante: "segun_vehiculo",
  requiere_variante_barco: false,
  modalidades: [],
});

const emptyModalidad = () => ({
  tipo: "regular",
  vehiculo: "bus",
  cupo_max: 54,
  cuenta_infante: false,
  horarios: [{ hora: "09:00", dias_semana: DIAS.map((d) => d.value) }],
});

export default function TourFormDialog({ open, onOpenChange, tour, onSaved }) {
  const [form, setForm] = useState(emptyTour());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (tour) {
        setForm({
          ...emptyTour(),
          ...tour,
          estacionalidad: {
            todo_el_anio: tour.estacionalidad?.todo_el_anio ?? true,
            desde: tour.estacionalidad?.desde || "",
            hasta: tour.estacionalidad?.hasta || "",
          },
          politica_cancelacion_horas:
            tour.politica_cancelacion_horas == null ? "" : String(tour.politica_cancelacion_horas),
          modalidades: tour.modalidades || [],
        });
      } else {
        setForm(emptyTour());
      }
    }
  }, [open, tour]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleIdioma = (val) => {
    setForm((f) => {
      const has = f.idiomas.includes(val);
      const idiomas = has ? f.idiomas.filter((i) => i !== val) : [...f.idiomas, val];
      return { ...f, idiomas: idiomas.length ? idiomas : f.idiomas };
    });
  };

  // ---- modalidades ----
  const addModalidad = () => set({ modalidades: [...form.modalidades, emptyModalidad()] });
  const removeModalidad = (i) => set({ modalidades: form.modalidades.filter((_, idx) => idx !== i) });
  const updateModalidad = (i, patch) =>
    set({ modalidades: form.modalidades.map((m, idx) => (idx === i ? { ...m, ...patch } : m)) });

  const addHorario = (mi) =>
    updateModalidad(mi, {
      horarios: [...form.modalidades[mi].horarios, { hora: "09:00", dias_semana: DIAS.map((d) => d.value) }],
    });
  const removeHorario = (mi, hi) =>
    updateModalidad(mi, { horarios: form.modalidades[mi].horarios.filter((_, idx) => idx !== hi) });
  const updateHorario = (mi, hi, patch) =>
    updateModalidad(mi, {
      horarios: form.modalidades[mi].horarios.map((h, idx) => (idx === hi ? { ...h, ...patch } : h)),
    });
  const toggleDia = (mi, hi, dia) => {
    const cur = form.modalidades[mi].horarios[hi].dias_semana || [];
    const next = cur.includes(dia) ? cur.filter((d) => d !== dia) : [...cur, dia];
    updateHorario(mi, hi, { dias_semana: next });
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) {
      toast.error("El nombre del tour es obligatorio.");
      return;
    }
    if (form.modalidades.length === 0) {
      toast.error("Agregá al menos una modalidad.");
      return;
    }
    const payload = {
      ...form,
      politica_cancelacion_horas:
        form.politica_cancelacion_horas === "" ? null : parseInt(form.politica_cancelacion_horas, 10),
      estacionalidad: {
        todo_el_anio: form.estacionalidad.todo_el_anio,
        desde: form.estacionalidad.todo_el_anio ? null : form.estacionalidad.desde || null,
        hasta: form.estacionalidad.todo_el_anio ? null : form.estacionalidad.hasta || null,
      },
      modalidades: form.modalidades.map((m) => ({
        ...m,
        cupo_max: parseInt(m.cupo_max, 10) || 0,
        horarios: m.horarios.map((h) => ({
          hora: h.hora,
          dias_semana: h.dias_semana,
          idioma: h.idioma || null,
          desde: h.desde || null,
          hasta: h.hasta || null,
        })),
      })),
    };
    setSaving(true);
    try {
      if (tour?.id) {
        await api.put(`/tours/${tour.id}`, payload);
        toast.success("Tour actualizado.");
      } else {
        await api.post("/tours", payload);
        toast.success("Tour creado.");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tour-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{tour ? "Editar tour" : "Nuevo tour"}</DialogTitle>
          <DialogDescription>
            Configurá categoría, idiomas, punto de recogida, modalidades (VIP / Regular / Privado), horarios,
            cupos por vehículo, regla de infantes y estacionalidad.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Datos generales */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label>Nombre del tour</Label>
              <Input
                value={form.nombre}
                onChange={(e) => set({ nombre: e.target.value })}
                placeholder="Ej: Washington DC"
                data-testid="tour-nombre-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Categoría</Label>
              <Select value={form.categoria} onValueChange={(v) => set({ categoria: v })}>
                <SelectTrigger data-testid="tour-categoria-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORIAS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Regla de conteo de infantes</Label>
              <Select value={form.regla_infante} onValueChange={(v) => set({ regla_infante: v })}>
                <SelectTrigger data-testid="tour-regla-infante-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(REGLAS_INFANTE).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Punto de recogida</Label>
              <Input
                value={form.punto_recogida}
                onChange={(e) => set({ punto_recogida: e.target.value })}
                data-testid="tour-recogida-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Idiomas</Label>
              <div className="flex gap-2">
                {Object.entries(IDIOMAS).map(([k, v]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => toggleIdioma(k)}
                    data-testid={`tour-idioma-${k}`}
                    className={`px-3 h-10 rounded-md text-sm border transition-colors ${
                      form.idiomas.includes(k)
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Política de cancelación (horas)</Label>
              <Input
                type="number"
                value={form.politica_cancelacion_horas}
                onChange={(e) => set({ politica_cancelacion_horas: e.target.value })}
                placeholder="Global (24h) si se deja vacío"
                data-testid="tour-politica-input"
              />
            </div>
          </div>

          {/* Estacionalidad */}
          <div className="rounded-md border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-800">Disponible todo el año</p>
                <p className="text-xs text-slate-500">Desactivá para definir un rango estacional.</p>
              </div>
              <Switch
                checked={form.estacionalidad.todo_el_anio}
                onCheckedChange={(v) => set({ estacionalidad: { ...form.estacionalidad, todo_el_anio: v } })}
                data-testid="tour-todo-anio-switch"
              />
            </div>
            {!form.estacionalidad.todo_el_anio && (
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-xs">Desde (MM-DD)</Label>
                  <Input
                    value={form.estacionalidad.desde}
                    onChange={(e) => set({ estacionalidad: { ...form.estacionalidad, desde: e.target.value } })}
                    placeholder="03-22"
                    data-testid="tour-desde-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Hasta (MM-DD)</Label>
                  <Input
                    value={form.estacionalidad.hasta}
                    onChange={(e) => set({ estacionalidad: { ...form.estacionalidad, hasta: e.target.value } })}
                    placeholder="11-30"
                    data-testid="tour-hasta-input"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Modalidades */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Modalidades, cupos y horarios</Label>
              <Button type="button" variant="outline" size="sm" onClick={addModalidad} data-testid="add-modalidad-button" className="gap-1">
                <Plus className="h-4 w-4" /> Modalidad
              </Button>
            </div>

            {form.modalidades.length === 0 && (
              <p className="text-sm text-slate-400 border border-dashed rounded-md p-4 text-center">
                Sin modalidades. Agregá al menos una (VIP / Regular / Privado).
              </p>
            )}

            {form.modalidades.map((m, mi) => (
              <div key={mi} className="border-l-2 border-slate-200 pl-4 ml-1 py-2" data-testid={`modalidad-${mi}`}>
                <div className="rounded-md border border-slate-200 p-4 space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Tipo</Label>
                        <Select value={m.tipo} onValueChange={(v) => updateModalidad(mi, { tipo: v })}>
                          <SelectTrigger data-testid={`modalidad-tipo-${mi}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(TIPOS_MODALIDAD).map(([k, v]) => (
                              <SelectItem key={k} value={k}>
                                {v}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Vehículo</Label>
                        <Select value={m.vehiculo} onValueChange={(v) => updateModalidad(mi, { vehiculo: v })}>
                          <SelectTrigger data-testid={`modalidad-vehiculo-${mi}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(VEHICULOS).map(([k, v]) => (
                              <SelectItem key={k} value={k}>
                                {v}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Cupo máx (PAX)</Label>
                        <Input
                          type="number"
                          value={m.cupo_max}
                          onChange={(e) => updateModalidad(mi, { cupo_max: e.target.value })}
                          data-testid={`modalidad-cupo-${mi}`}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Infante cuenta</Label>
                        <div className="h-10 flex items-center">
                          <Switch
                            checked={m.cuenta_infante}
                            onCheckedChange={(v) => updateModalidad(mi, { cuenta_infante: v })}
                            data-testid={`modalidad-infante-${mi}`}
                          />
                        </div>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeModalidad(mi)}
                      data-testid={`remove-modalidad-${mi}`}
                      className="text-slate-400 hover:text-red-600 shrink-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Horarios */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" /> Horarios
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addHorario(mi)}
                        data-testid={`add-horario-${mi}`}
                        className="h-7 gap-1 text-xs"
                      >
                        <Plus className="h-3.5 w-3.5" /> Horario
                      </Button>
                    </div>

                    {m.horarios.map((h, hi) => (
                      <div key={hi} className="bg-slate-50 rounded-md p-3 space-y-2" data-testid={`horario-${mi}-${hi}`}>
                        <div className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={h.hora}
                            onChange={(e) => updateHorario(mi, hi, { hora: e.target.value })}
                            className="w-32 bg-white"
                            data-testid={`horario-hora-${mi}-${hi}`}
                          />
                          <div className="flex flex-wrap gap-1 flex-1">
                            {DIAS.map((d) => {
                              const active = (h.dias_semana || []).includes(d.value);
                              return (
                                <button
                                  key={d.value}
                                  type="button"
                                  onClick={() => toggleDia(mi, hi, d.value)}
                                  data-testid={`dia-${mi}-${hi}-${d.value}`}
                                  className={`w-9 h-8 rounded text-xs font-medium border transition-colors ${
                                    active
                                      ? "bg-slate-900 text-white border-slate-900"
                                      : "bg-white text-slate-500 border-slate-200 hover:bg-slate-100"
                                  }`}
                                >
                                  {d.label}
                                </button>
                              );
                            })}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeHorario(mi, hi)}
                            data-testid={`remove-horario-${mi}-${hi}`}
                            className="text-slate-400 hover:text-red-600 h-8 w-8 shrink-0"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 pl-1 pt-1">
                          <span className="text-[11px] uppercase tracking-wide text-slate-400">Idioma</span>
                          <Select
                            value={h.idioma || "_"}
                            onValueChange={(v) => updateHorario(mi, hi, { idioma: v === "_" ? null : v })}
                          >
                            <SelectTrigger className="h-8 w-32 bg-white text-xs" data-testid={`horario-idioma-${mi}-${hi}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_">Según el tour</SelectItem>
                              {Object.entries(IDIOMAS).map(([k, v]) => (
                                <SelectItem key={k} value={k}>
                                  {v}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <span className="text-[11px] uppercase tracking-wide text-slate-400 ml-2">Temporada</span>
                          <Input
                            value={h.desde || ""}
                            onChange={(e) => updateHorario(mi, hi, { desde: e.target.value })}
                            placeholder="Desde MM-DD"
                            className="h-8 w-28 bg-white text-xs"
                            data-testid={`horario-desde-${mi}-${hi}`}
                          />
                          <Input
                            value={h.hasta || ""}
                            onChange={(e) => updateHorario(mi, hi, { hasta: e.target.value })}
                            placeholder="Hasta MM-DD"
                            className="h-8 w-28 bg-white text-xs"
                            data-testid={`horario-hasta-${mi}-${hi}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Variante SIN/CON BARCO (Cataratas del Niágara) */}
          <div className="flex items-center justify-between rounded-md border border-slate-200 p-4">
            <div>
              <p className="text-sm font-medium text-slate-800">Tiene variante SIN / CON BARCO</p>
              <p className="text-xs text-slate-500">
                Cada reserva de este tour elige "sin barco" o "con barco" y la tarifa cambia según eso
                (ej. Cataratas del Niágara). No aplica a la mayoría de los tours.
              </p>
            </div>
            <Switch
              checked={!!form.requiere_variante_barco}
              onCheckedChange={(v) => set({ requiere_variante_barco: v })}
              data-testid="tour-variante-barco-switch"
            />
          </div>

          {/* Activo */}
          <div className="flex items-center justify-between rounded-md border border-slate-200 p-4">
            <div>
              <p className="text-sm font-medium text-slate-800">Tour activo</p>
              <p className="text-xs text-slate-500">Los tours inactivos no aparecen para nuevas reservas.</p>
            </div>
            <Switch checked={form.activo} onCheckedChange={(v) => set({ activo: v })} data-testid="tour-activo-switch" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="tour-form-cancel">
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="tour-form-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar tour"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
